import {useEffect} from 'react'

import {AsyncValue} from './types'
import useDependentReinitializingState_discouraged from './useDependentReinitializingState_discouraged'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function defaultParse(response: Response): Promise<any> {
  return response.json()
}

/**
 * Fetches a resource from a URL and tracks its resolution status.
 *
 * The API mirrors the global `fetch` function, with an additional parameter to support
 * asynchronous parsing of the response. By default, the response is parsed as JSON.
 *
 * Note that an unsucessful HTTP status code in a response will not inherently result in an error
 * state. This behavior can be customized by throwing in the parse function.
 *
 * This hook should only be used to fetch resources that will not trigger mutations, because no
 * guarantees are made about how many requests will be made.
 *
 * @param url The URL of the resource to be fetched.
 * @param init The initialization options for the request. Should be memoized.
 * @param parse A function to coerce the response to a value. Should be memoized.
 * @returns A representation of the state of the current fetch response.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch
 * @see https://tacklebox.beta-p.kensho.com/useFetch
 */
export default function useFetch<T>(
  url: string,
  init?: RequestInit,
  parse: (response: Response) => Promise<T> = defaultParse
): AsyncValue<T> {
  const [asyncValue, setAsyncValue] = useDependentReinitializingState_discouraged<AsyncValue<T>>(
    {status: 'pending'},
    [url, init, parse]
  )

  useEffect(() => {
    let isCurrent = true
    const abortController = new AbortController()
    const {signal} = abortController

    fetch(url, {signal, ...init})
      .then(parse)
      .then((value) => isCurrent && setAsyncValue({status: 'ready', value}))
      .catch((error) => isCurrent && setAsyncValue({status: 'error', error}))

    return () => {
      isCurrent = false
      abortController.abort()
    }
  }, [init, parse, setAsyncValue, url])

  return asyncValue
}
